import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import type { DateRangeValue } from '@/components/ui/date-range-picker'
import { Select } from '@/components/ui/select'
import { fetchUnits } from '@/api/units.api'
import { FilterFormWrapper } from '@/components/FilterFormWrapper'

export interface FloorplanFilterValues {
  keySearch: string
  unitId: string | undefined
  unitName?: string
  time: DateRangeValue | undefined
}

interface FloorplanFilterPanelProps {
  values: FloorplanFilterValues
  onChange: (values: FloorplanFilterValues) => void
  onSearch: () => void
  showUnitColumn: boolean
}

export function FloorplanFilterPanel({
  values,
  onChange,
  onSearch,
  showUnitColumn,
}: FloorplanFilterPanelProps) {
  function set<TKey extends keyof FloorplanFilterValues>(
    key: TKey,
    value: FloorplanFilterValues[TKey],
  ) {
    onChange({ ...values, [key]: value })
  }

  return (
    <FilterFormWrapper
      onSearch={onSearch}
      className="rounded-xl border border-gray-300 bg-white px-6 py-5"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex min-w-[200px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Tên sơ đồ</span>
            <Input
              placeholder="Nhập từ khóa"
              value={values.keySearch}
              onChange={(e: { target: { value: string } }) =>
                set('keySearch', e.target.value)
              }
            />
          </div>
          {showUnitColumn && (
            <div className="flex min-w-[200px] flex-1 flex-col gap-1">
              <span className="text-sm font-medium">Đơn vị</span>
              <Select
                initialPage={0}
                lazy
                placeholder="Tất cả đơn vị"
                value={values.unitId ?? undefined}
                className="w-full"
                cacheKey="floorplan-filter-unit-select"
                enableRemoteSearch
                allLabel="Tất cả đơn vị"
                initialOptions={
                  values.unitId && values.unitName
                    ? [{ value: values.unitId, label: values.unitName }]
                    : []
                }
                loadOptions={async ({ page, signal, searchText }) => {
                  const res = await fetchUnits(
                    page,
                    10,
                    { keySearch: searchText?.trim() || undefined },
                    signal,
                  )
                  const arr = Array.isArray(res.items) ? res.items : []
                  const pageSize = 10
                  const hasNextPage = arr.length >= pageSize

                  return {
                    options: arr.map((u: any) => ({
                      value: u.id,
                      label: u.name,
                    })),
                    hasNextPage,
                    nextPage: hasNextPage ? page + 1 : undefined,
                  }
                }}
                onLazyValueChange={(unitId, option) => {
                  onChange({
                    ...values,
                    unitId: unitId,
                    unitName: option?.label ?? undefined,
                  })
                }}
              />
            </div>
          )}
          <div className="flex min-w-[200px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Thời gian</span>
            <DateRangePicker
              value={values.time}
              onChange={(v) => set('time', v)}
              className="w-full"
            />
          </div>
          <Button
            variant="outline"
            size="md"
            onClick={onSearch}
            className="border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10"
          >
            <Search size={16} />
            Tìm kiếm
          </Button>
        </div>
      </div>
    </FilterFormWrapper>
  )
}
